import { useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "@/lib/lang";
import { mergeDocWithText } from "@/lib/documentCache";
import { useDocument, useDocumentFileUrl } from "@/hooks/queries";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Markdown from "@/components/Markdown";
import { FileText, Loader2, Search, Download, ExternalLink } from "lucide-react";

function normalizePageOffsets(offsets) {
  let normalized = [...offsets];
  while (normalized.length > 1 && normalized[0] === normalized[1]) {
    normalized = normalized.slice(1);
  }
  return normalized;
}

function stripLlamaParseHtml(text) {
  return (text || "")
    .replace(/<\/?u>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n");
}

function buildPages(doc) {
  const text = doc?.text || "";
  let offsets = doc?.page_offsets?.length ? normalizePageOffsets(doc.page_offsets) : [0];
  if (!text && doc?.pages > 0) {
    return Array.from({ length: Math.max(doc.pages, 1) }, (_, i) => ({
      page: i + 1,
      start: 0,
      text: "",
    }));
  }
  if (offsets.length <= 1 && doc?.pages > 1) {
    const size = Math.ceil(text.length / doc.pages);
    const pages = [];
    for (let i = 0; i < doc.pages; i += 1) {
      pages.push({ page: i + 1, start: i * size, text: text.slice(i * size, (i + 1) * size) });
    }
    return pages;
  }
  const pages = [];
  for (let i = 0; i < offsets.length; i += 1) {
    const start = offsets[i];
    const end = i + 1 < offsets.length ? offsets[i + 1] : text.length;
    pages.push({ page: i + 1, start, text: text.slice(start, end) });
  }
  return pages.length ? pages : [{ page: 1, start: 0, text }];
}

function isPdf(doc, fileMeta) {
  const type = fileMeta?.content_type || doc?.content_type || "";
  return doc?.file_type === "pdf" || type.includes("pdf");
}

function isImage(doc, fileMeta) {
  const type = fileMeta?.content_type || doc?.content_type || "";
  return doc?.file_type === "image" || type.startsWith("image/");
}

function isDocx(doc, fileMeta) {
  const type = fileMeta?.content_type || doc?.content_type || "";
  return doc?.file_type === "docx" || type.includes("wordprocessingml") || type.includes("msword");
}

export default function DocumentViewer({ documentId, highlightTarget, onReady }) {
  const { t, lang } = useLang();
  const [viewMode, setViewMode] = useState("original");
  const [currentPage, setCurrentPage] = useState(1);
  const [findQuery, setFindQuery] = useState("");
  const highlightRef = useRef(null);
  const onReadyRef = useRef(onReady);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const { data: meta, isLoading: metaLoading } = useDocument(documentId, { includeText: false });
  const needsFullText =
    viewMode === "text" || (meta != null && !meta.has_file);
  const { data: fullDoc, isLoading: textLoading } = useDocument(documentId, {
    includeText: true,
    enabled: Boolean(documentId) && needsFullText && meta != null,
  });
  const { data: fileMeta, isLoading: fileLoading, error: fileQueryError } = useDocumentFileUrl(
    documentId,
    { enabled: Boolean(documentId) && Boolean(meta?.has_file) },
  );

  const doc = useMemo(() => {
    if (!meta) return null;
    if (fullDoc?.text != null) return fullDoc;
    return mergeDocWithText(meta, null);
  }, [meta, fullDoc]);

  const fileError = fileQueryError?.message || null;

  useEffect(() => {
    if (!documentId) return;
    setCurrentPage(1);
    setFindQuery("");
    setViewMode(meta?.has_file ? "original" : "text");
  }, [documentId, meta?.has_file]);

  useEffect(() => {
    if (doc) onReadyRef.current?.(doc);
  }, [doc]);

  const pages = useMemo(() => buildPages(doc), [doc]);

  useEffect(() => {
    if (highlightTarget?.page) {
      setCurrentPage(highlightTarget.page);
      setViewMode("text");
    } else if (highlightTarget?.char_start != null && pages.length) {
      const page = pages.find((p, idx) => {
        const end = idx + 1 < pages.length ? pages[idx + 1].start : (doc?.text?.length || 0);
        return highlightTarget.char_start >= p.start && highlightTarget.char_start < end;
      });
      if (page) {
        setCurrentPage(page.page);
        setViewMode("text");
      }
    }
  }, [highlightTarget, pages, doc]);

  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentPage, highlightTarget, viewMode]);

  const activePage = pages.find((p) => p.page === currentPage) || pages[0];
  const displayText = stripLlamaParseHtml(activePage?.text || "");

  const renderedText = useMemo(() => {
    if (textLoading) {
      return (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 size={18} className="animate-spin" />
        </div>
      );
    }
    if (!findQuery.trim()) {
      return (
        <Markdown
          text={displayText}
          className="text-sm leading-relaxed text-slate-800 font-serif-display"
        />
      );
    }
    const q = findQuery.trim();
    const idx = displayText.indexOf(q);
    if (idx === -1) {
      return (
        <Markdown
          text={displayText}
          className="text-sm leading-relaxed text-slate-800 font-serif-display"
        />
      );
    }
    return (
      <>
        {displayText.slice(0, idx)}
        <mark className="bg-yellow-200">{displayText.slice(idx, idx + q.length)}</mark>
        {displayText.slice(idx + q.length)}
      </>
    );
  }, [displayText, findQuery, textLoading]);

  const renderOriginal = () => {
    if (!doc?.has_file) {
      return (
        <div className="h-full flex items-center justify-center text-sm text-slate-400 px-6 text-center">
          {t("viewer_no_file")}
        </div>
      );
    }
    if (fileLoading) {
      return (
        <div className="h-full flex items-center justify-center text-slate-400">
          <Loader2 size={18} className="animate-spin" />
        </div>
      );
    }
    if (fileError || !fileMeta?.url) {
      return (
        <div className="h-full flex items-center justify-center text-sm text-slate-400 px-6 text-center">
          {fileError || t("viewer_no_file")}
        </div>
      );
    }

    if (isPdf(doc, fileMeta)) {
      return (
        <iframe
          title={doc.filename}
          src={fileMeta.url}
          className="w-full h-full border-0 bg-white"
          data-testid="document-viewer-pdf"
        />
      );
    }

    if (isImage(doc, fileMeta)) {
      return (
        <div className="h-full overflow-auto flex items-start justify-center p-4 bg-slate-100">
          <img
            src={fileMeta.url}
            alt={doc.filename}
            className="max-w-full h-auto shadow-sm"
            data-testid="document-viewer-image"
          />
        </div>
      );
    }

    if (isDocx(doc, fileMeta)) {
      return (
        <div className="h-full flex flex-col items-center justify-center gap-4 px-6 text-center">
          <FileText size={32} className="text-slate-400" />
          <p className="text-sm text-slate-600 max-w-sm">{t("viewer_docx_hint")}</p>
          <Button asChild variant="outline" className="rounded-none gap-2">
            <a href={fileMeta.url} download={fileMeta.filename || doc.filename} target="_blank" rel="noreferrer">
              <Download size={14} />
              {t("viewer_download")}
            </a>
          </Button>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col">
        <iframe
          title={doc.filename}
          src={fileMeta.url}
          className="flex-1 w-full border-0 bg-white"
          data-testid="document-viewer-file"
        />
        <div className="grid-border-t px-4 py-2 bg-white flex justify-end">
          <Button asChild variant="ghost" size="sm" className="rounded-none gap-1 text-xs">
            <a href={fileMeta.url} target="_blank" rel="noreferrer">
              <ExternalLink size={12} />
              {t("viewer_open_original")}
            </a>
          </Button>
        </div>
      </div>
    );
  };

  if (!documentId) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-400" data-testid="document-viewer-empty">
        {t("select_document")}
      </div>
    );
  }

  if (metaLoading && !doc) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400" data-testid="document-viewer-loading">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50/30" data-testid="document-viewer">
      <div className="grid-border-b px-4 py-3 bg-white flex items-center gap-3">
        <FileText size={14} className="text-slate-500 flex-shrink-0" />
        <span className="text-xs font-medium text-slate-900 truncate flex-1">{doc?.filename}</span>
        {metaLoading && (
          <Loader2 size={14} className="animate-spin text-slate-400 flex-shrink-0" />
        )}
        {viewMode === "text" && (
          <span className="font-mono-tight text-[10px] text-slate-500">
            {currentPage}/{pages.length} {t("pages")}
          </span>
        )}
      </div>

      <div className="grid-border-b px-4 py-2 bg-white flex items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="doc-viewer-mode-original"
            onClick={() => setViewMode("original")}
            disabled={!doc?.has_file}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 ${
              viewMode === "original" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            } disabled:opacity-40`}
          >
            {t("viewer_original")}
          </button>
          <button
            type="button"
            data-testid="doc-viewer-mode-text"
            onClick={() => setViewMode("text")}
            className={`text-[10px] uppercase tracking-wider px-2 py-1 ${
              viewMode === "text" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {t("viewer_text")}
          </button>
        </div>
        {viewMode === "text" && (
          <>
            <Search size={13} className="text-slate-400 ms-auto" />
            <Input
              data-testid="doc-viewer-search"
              value={findQuery}
              onChange={(e) => setFindQuery(e.target.value)}
              placeholder={t("viewer_search")}
              className="h-8 border-0 focus-visible:ring-0 text-xs bg-transparent max-w-[180px]"
              dir={lang === "ar" ? "rtl" : "ltr"}
            />
          </>
        )}
        {viewMode === "original" && fileMeta?.url && (
          <Button asChild variant="ghost" size="sm" className="ms-auto rounded-none h-7 px-2 text-[10px] gap-1">
            <a href={fileMeta.url} download={fileMeta.filename || doc?.filename} target="_blank" rel="noreferrer">
              <Download size={12} />
              {t("viewer_download")}
            </a>
          </Button>
        )}
      </div>

      {viewMode === "original" ? (
        <div className="flex-1 overflow-hidden">{renderOriginal()}</div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto px-6 py-6 paper-grain">
            <div
              ref={highlightTarget ? highlightRef : null}
              className="text-sm leading-relaxed text-slate-800 font-serif-display"
              dir={doc?.language === "en" ? "ltr" : "rtl"}
              data-testid="document-viewer-content"
            >
              {renderedText}
            </div>
          </div>

          <div className="grid-border-t px-4 py-2 bg-white flex items-center justify-between gap-2">
            <button
              type="button"
              data-testid="doc-viewer-prev"
              disabled={currentPage <= 1 || textLoading}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="text-xs text-slate-600 disabled:opacity-40 hover:text-slate-900"
            >
              {t("viewer_prev")}
            </button>
            <span className="font-mono-tight text-[10px] text-slate-500">
              {t("viewer_page")} {currentPage}
            </span>
            <button
              type="button"
              data-testid="doc-viewer-next"
              disabled={currentPage >= pages.length || textLoading}
              onClick={() => setCurrentPage((p) => Math.min(pages.length, p + 1))}
              className="text-xs text-slate-600 disabled:opacity-40 hover:text-slate-900"
            >
              {t("viewer_next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
